import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Alert,
  Stack,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Divider,
  Chip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const STEPS = [
  'Request',
  'Contact',
  'Approve',
  'Assign',
  'Progress',
  'Withdraw',
  'Final $',
  'Closed',
];

function contactLabel(c) {
  if (!c) return '';
  return `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || '';
}

function providerLabel(p) {
  if (!p) return '—';
  const person = contactLabel(p.contact);
  if (p.provider_kind === 'company' && (p.company?.company_name || p.company?.name)) {
    return `${person} · ${p.company.company_name || p.company.name}`;
  }
  return person || p.provider_number || 'Provider';
}

export default function JobSupportSeekerWorkflowDrawer({
  requestId,
  open,
  onClose,
  onUpdated,
  providers = [],
  termsText = '',
  termsVersion = '',
}) {
  const { hasPermission, isAdmin, user } = useAuth();
  const isStaff = isAdmin || hasPermission('helpdesk_support') || hasPermission('helpdesk_modify');
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [termsDlg, setTermsDlg] = useState({ open: false, receiptId: null });
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [form, setForm] = useState({
    identification_notes: '',
    provider_id: '',
    monthly_collected_amount: '',
    monthly_paid_to_provider: '',
    payment_notes: '',
    withdraw_reason: '',
    final_amount_collected: '',
    extension_kind: 'month_to_month',
    months_to_extend: 1,
    new_end_date: '',
    extension_reason: '',
    period_year: new Date().getFullYear(),
    period_month: new Date().getMonth() + 1,
    service_summary: '',
    amount_collected: '',
    amount_paid_to_provider: '',
  });

  const load = useCallback(async () => {
    if (!requestId || !open) return;
    setLoading(true);
    setErr('');
    try {
      const r = await api.get(`/helpdesk/job-support/${requestId}`);
      const d = r.data?.data || null;
      setRow(d);
      setForm((p) => ({
        ...p,
        identification_notes: d?.identification_notes || '',
        provider_id: d?.provider_id || '',
        monthly_collected_amount: d?.monthly_collected_amount ?? '',
        monthly_paid_to_provider: d?.monthly_paid_to_provider ?? '',
        payment_notes: d?.payment_notes || '',
        final_amount_collected: d?.final_amount_collected ?? '',
      }));
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to load');
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [requestId, open]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const run = async (action, extra = {}) => {
    if (!row?.id) return;
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const r = await api.post(`/helpdesk/job-support/${row.id}/workflow`, { action, ...form, ...extra });
      setRow(r.data?.data || null);
      setMsg(r.data?.message || 'Updated');
      onUpdated?.();
      await load();
    } catch (e) {
      setErr(e.response?.data?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const step = Number(row?.workflow_step || 1);
  const isRequester = row?.created_by === user?.id;
  const userEmail = String(user?.email || '')
    .trim()
    .toLowerCase();
  const providerContactEmail = String(row?.provider?.contact?.email || '')
    .trim()
    .toLowerCase();
  const isAssignedProvider =
    Boolean(row?.provider) &&
    (row.provider.created_by === user?.id ||
      (userEmail && providerContactEmail && userEmail === providerContactEmail));
  const canDecideExtensions = isStaff || isAssignedProvider;
  const canApproveReceipt = isStaff || isAssignedProvider;

  const pendingExtensions = useMemo(
    () => (row?.extensions || []).filter((e) => e.status === 'pending'),
    [row]
  );
  const pendingReceipts = useMemo(
    () => (row?.paymentReceipts || []).filter((e) => e.status === 'pending_provider'),
    [row]
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => !busy && onClose()}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 2 } }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
          Job Support Seeker workflow
        </Typography>
        {row && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {row.request_number} · {contactLabel(row.contact)}
            {row.provider ? ` · Provider: ${providerLabel(row.provider)}` : ''}
          </Typography>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && row && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Step {step}: <strong>{STEPS[step - 1] || row.status}</strong>
              {row.expected_start_date || row.expected_end_date
                ? ` · ${row.expected_start_date || '—'} → ${row.expected_end_date || '—'}`
                : ''}
            </Alert>

            <Stepper activeStep={Math.max(0, step - 1)} alternativeLabel sx={{ mb: 2 }}>
              {STEPS.map((label) => (
                <Step key={label} completed={step > STEPS.indexOf(label) + 1}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {err && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>
                {err}
              </Alert>
            )}
            {msg && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>
                {msg}
              </Alert>
            )}

            <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
              Responsibility: {row.job_responsibility || '—'}
              {'\n'}Skills needed: {row.skills_needed || '—'}
              {'\n'}Pay: {row.pay_amount ?? '—'} / {row.pay_period || '—'}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {step === 1 && isStaff && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                <TextField
                  label="Identification notes"
                  fullWidth
                  multiline
                  minRows={3}
                  value={form.identification_notes}
                  onChange={(e) => patch('identification_notes', e.target.value)}
                />
                <Button variant="contained" disabled={busy} onClick={() => run('contact')}>
                  Step 2 — Contacted & identified
                </Button>
              </Stack>
            )}

            {step === 2 && isStaff && (
              <Button variant="contained" disabled={busy} onClick={() => run('approve')} sx={{ mb: 2 }}>
                Step 3 — Approve
              </Button>
            )}

            {step === 3 && isStaff && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                <TextField
                  select
                  label="Job Support Provider"
                  fullWidth
                  value={form.provider_id}
                  onChange={(e) => patch('provider_id', e.target.value)}
                >
                  <MenuItem value="">Select…</MenuItem>
                  {providers.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {providerLabel(p)}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  disabled={busy || !form.provider_id}
                  onClick={() => run('assign', { provider_id: form.provider_id })}
                >
                  Step 4 — Assign provider
                </Button>
              </Stack>
            )}

            {step === 4 && isStaff && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {String(row.pay_period).toLowerCase() === 'month' && (
                  <>
                    <TextField
                      label="Monthly collected"
                      type="number"
                      value={form.monthly_collected_amount}
                      onChange={(e) => patch('monthly_collected_amount', e.target.value)}
                    />
                    <TextField
                      label="Paid to provider"
                      type="number"
                      value={form.monthly_paid_to_provider}
                      onChange={(e) => patch('monthly_paid_to_provider', e.target.value)}
                    />
                  </>
                )}
                <Button variant="contained" disabled={busy} onClick={() => run('start_progress')}>
                  Step 5 — In progress
                </Button>
              </Stack>
            )}

            {step === 5 && (
              <Stack spacing={2} sx={{ mb: 2 }}>
                {(isRequester || isStaff) && (
                  <>
                    <Typography variant="subtitle2">Extend support</Typography>
                    <TextField
                      select
                      label="Extension type"
                      value={form.extension_kind}
                      onChange={(e) => patch('extension_kind', e.target.value)}
                    >
                      <MenuItem value="month_to_month">Month to month</MenuItem>
                      <MenuItem value="date_range">New end date</MenuItem>
                    </TextField>
                    {form.extension_kind === 'month_to_month' ? (
                      <TextField
                        label="Months to extend"
                        type="number"
                        value={form.months_to_extend}
                        onChange={(e) => patch('months_to_extend', e.target.value)}
                      />
                    ) : (
                      <TextField
                        label="New end date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={form.new_end_date}
                        onChange={(e) => patch('new_end_date', e.target.value)}
                      />
                    )}
                    <TextField
                      label="Reason"
                      multiline
                      minRows={2}
                      value={form.extension_reason}
                      onChange={(e) => patch('extension_reason', e.target.value)}
                    />
                    <Button
                      variant="outlined"
                      disabled={busy}
                      onClick={() =>
                        run('request_extension', {
                          extension_kind: form.extension_kind,
                          months_to_extend: form.months_to_extend,
                          new_end_date: form.new_end_date,
                          reason: form.extension_reason,
                        })
                      }
                    >
                      Request extension
                    </Button>
                  </>
                )}

                {canDecideExtensions &&
                  pendingExtensions.map((ex) => (
                    <Alert
                      key={ex.id}
                      severity="warning"
                      action={
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            size="small"
                            onClick={() => run('approve_extension', { extension_id: ex.id })}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() => run('reject_extension', { extension_id: ex.id })}
                          >
                            Reject
                          </Button>
                        </Stack>
                      }
                    >
                      Extension pending: {ex.extension_kind}
                      {ex.months_to_extend ? ` +${ex.months_to_extend} mo` : ''}
                      {ex.new_end_date ? ` → ${ex.new_end_date}` : ''}
                      {ex.reason ? ` — ${ex.reason}` : ''}
                    </Alert>
                  ))}

                {isStaff && (
                  <>
                    <Divider />
                    <Typography variant="subtitle2">Monthly payment / service record</Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="Year"
                        type="number"
                        value={form.period_year}
                        onChange={(e) => patch('period_year', e.target.value)}
                        sx={{ width: 100 }}
                      />
                      <TextField
                        label="Month"
                        type="number"
                        inputProps={{ min: 1, max: 12 }}
                        value={form.period_month}
                        onChange={(e) => patch('period_month', e.target.value)}
                        sx={{ width: 100 }}
                      />
                    </Stack>
                    <TextField
                      label="Service rendered summary"
                      multiline
                      minRows={2}
                      value={form.service_summary}
                      onChange={(e) => patch('service_summary', e.target.value)}
                    />
                    <TextField
                      label="Amount collected"
                      type="number"
                      value={form.amount_collected}
                      onChange={(e) => patch('amount_collected', e.target.value)}
                    />
                    <TextField
                      label="Amount paid to provider"
                      type="number"
                      value={form.amount_paid_to_provider}
                      onChange={(e) => patch('amount_paid_to_provider', e.target.value)}
                    />
                    <Button
                      variant="contained"
                      disabled={busy}
                      onClick={() =>
                        run('create_payment_receipt', {
                          period_year: form.period_year,
                          period_month: form.period_month,
                          service_summary: form.service_summary,
                          amount_collected: form.amount_collected,
                          amount_paid_to_provider: form.amount_paid_to_provider,
                        })
                      }
                    >
                      Create monthly receipt (await provider approve)
                    </Button>
                  </>
                )}

                {pendingReceipts.map((rc) => (
                  <Alert
                    key={rc.id}
                    severity="info"
                    action={
                      canApproveReceipt ? (
                        <Button
                          size="small"
                          onClick={() => {
                            setAcceptTerms(false);
                            setTermsDlg({ open: true, receiptId: rc.id });
                          }}
                        >
                          Approve receipt
                        </Button>
                      ) : null
                    }
                  >
                    {rc.period_label || `${rc.period_year}-${rc.period_month}`}: paid{' '}
                    {rc.amount_paid_to_provider ?? '—'} {rc.currency || 'USD'}
                    {rc.service_summary ? ` — ${rc.service_summary}` : ''}
                    {!canApproveReceipt ? ' (awaiting provider)' : ''}
                  </Alert>
                ))}

                <TextField
                  label="Withdraw reason"
                  multiline
                  minRows={2}
                  value={form.withdraw_reason}
                  onChange={(e) => patch('withdraw_reason', e.target.value)}
                />
                <Button color="warning" variant="contained" disabled={busy} onClick={() => run('request_withdraw')}>
                  Step 6 — Request stop / withdraw
                </Button>
              </Stack>
            )}

            {step === 6 && isStaff && (
              <Button variant="contained" disabled={busy} onClick={() => run('approve_withdraw')} sx={{ mb: 2 }}>
                Approve withdraw → Step 7
              </Button>
            )}

            {step === 7 && isStaff && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                <TextField
                  label="Final amount collected"
                  type="number"
                  value={form.final_amount_collected}
                  onChange={(e) => patch('final_amount_collected', e.target.value)}
                />
                <Button variant="outlined" disabled={busy} onClick={() => run('final_collect')}>
                  Record final collection
                </Button>
                <Button color="success" variant="contained" disabled={busy} onClick={() => run('close')}>
                  Step 8 — Close
                </Button>
              </Stack>
            )}

            {(row.extensions || []).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Extensions
                </Typography>
                {(row.extensions || []).map((ex) => (
                  <Chip
                    key={ex.id}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                    label={`${ex.status}: ${ex.extension_kind}${ex.new_end_date ? ` → ${ex.new_end_date}` : ''}`}
                    color={ex.status === 'approved' ? 'success' : ex.status === 'pending' ? 'warning' : 'default'}
                  />
                ))}
              </Box>
            )}

            {(row.paymentReceipts || []).length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Payment receipts
                </Typography>
                {(row.paymentReceipts || []).map((rc) => (
                  <Chip
                    key={rc.id}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                    label={`${rc.period_label || `${rc.period_year}-${rc.period_month}`}: ${rc.status}`}
                    color={rc.status === 'provider_approved' ? 'success' : 'default'}
                  />
                ))}
              </Box>
            )}

            {step < 8 && row.status !== 'cancelled' && isStaff && (
              <Button color="error" size="small" disabled={busy} onClick={() => run('cancel')} sx={{ mt: 2 }}>
                Cancel request
              </Button>
            )}

            <Button fullWidth sx={{ mt: 3 }} onClick={onClose} disabled={busy}>
              Close
            </Button>
          </>
        )}
      </Drawer>

      <Dialog open={termsDlg.open} onClose={() => setTermsDlg({ open: false, receiptId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Approve receipt of funds</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
            {termsText ||
              'I acknowledge that I have received payment for job support services for the stated period.'}
          </Typography>
          {termsVersion && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Terms version: {termsVersion}
            </Typography>
          )}
          <FormControlLabel
            control={<Checkbox checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />}
            label="I have read and agree to these terms and confirm receipt of funds"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermsDlg({ open: false, receiptId: null })}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!acceptTerms || busy}
            onClick={async () => {
              await run('approve_payment_receipt', {
                receipt_id: termsDlg.receiptId,
                accept_terms: true,
              });
              setTermsDlg({ open: false, receiptId: null });
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
